import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { format, addDays } from 'date-fns';
import { Calendar, Clock, User, Phone, Info, Mail, Link, Upload, PoundSterling, Loader2, AlertCircle } from 'lucide-react';
import BookingCalendar from '../components/booking/BookingCalendar';
import TimeSlotPicker from '../components/booking/TimeSlotPicker';
import ServiceSelector from '../components/booking/ServiceSelector';
import { useWebsiteLock } from '../contexts/WebsiteLockContext';
import { fetchBookingSettings, fetchServiceDetails, submitBooking, fetchFullyBookedDates, fetchPublicSettings, fetchAvailabilityPeriods } from '../services/api';
import { ServiceType } from '../types';

const BookingPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedService, setSelectedService] = useState<ServiceType | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [selectedTime, setSelectedTime] = useState('');
  const [acceptedPolicy, setAcceptedPolicy] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    phone: '',
    email: '',
    partySize: 1,
    notes: '',
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [showPaymentSection, setShowPaymentSection] = useState(false);
  const [bookingSubmitted, setBookingSubmitted] = useState(false);
  const [submittedBookingId, setSubmittedBookingId] = useState<number | null>(null);
  const [isLoadingService, setIsLoadingService] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [maxAdvanceBookingDays, setMaxAdvanceBookingDays] = useState(90);
  const [timeSlotSettings, setTimeSlotSettings] = useState({
    business_hours_start: '09:00:00',
    business_hours_end: '18:00:00',
    time_slot_duration: 60
  });
  const [fullyBookedDates, setFullyBookedDates] = useState<string[]>([]);
  const [lockedDates, setLockedDates] = useState<string[]>([]);

  useEffect(() => {
    const fetchBookingSettingsData = async () => {
      try {
        const settings = await fetchPublicSettings();
        setMaxAdvanceBookingDays(Number(settings?.max_advance_booking_days ?? 90));
        setTimeSlotSettings({
          business_hours_start: settings?.business_hours_start || '09:00:00',
          business_hours_end: settings?.business_hours_end || '18:00:00',
          time_slot_duration: Number(settings?.time_slot_duration ?? 60)
        });
      } catch (error) {
        console.error('Unable to load booking window settings:', error);
      }
    };

    const fetchBookedDates = async () => {
      try {
        const today = new Date();
        const maxDate = addDays(today, 90);
        const booked = await fetchFullyBookedDates(
          format(today, 'yyyy-MM-dd'),
          format(maxDate, 'yyyy-MM-dd')
        );
        setFullyBookedDates(booked);
      } catch (error) {
        console.error('Unable to load booked dates:', error);
      }
    };

    const fetchLockedDates = async () => {
      try {
        const periods = await fetchAvailabilityPeriods();
        const locked = periods
          .filter((p: any) => p.status === 'closed')
          .map((p: any) => `${p.start_date}|${p.end_date}`);
        setLockedDates(locked);
      } catch (error) {
        console.error('Unable to load locked dates:', error);
      }
    };

    fetchBookingSettingsData();
    fetchBookedDates();
    fetchLockedDates();

    const loadInitialData = async () => {
      const savedDetails = localStorage.getItem('customerDetails');
      if (savedDetails) {
        const details = JSON.parse(savedDetails);
        setFormData(prev => ({
          ...prev,
          fullName: details.fullName || '',
          phone: details.phone || '',
          email: details.email || '',
        }));
      }

      if (location.state?.selectedService) {
        const serviceId = location.state.selectedService;
        const serviceDetails = await fetchServiceDetails(serviceId);
        if (serviceDetails) {
          setSelectedService(serviceDetails);
          setCurrentStep(2);
        }
      }
    };

    loadInitialData();
  }, [location.state]);

  const handleServiceSelect = async (serviceId: number) => {
    setIsLoadingService(true);
    try {
      const serviceDetails = await fetchServiceDetails(serviceId);
      if (serviceDetails) {
        setSelectedService(serviceDetails);
        setCurrentStep(2);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } catch (error) {
      console.error('Error fetching service details:', error);
    } finally {
      setIsLoadingService(false);
    }
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};
    
    if (!formData.fullName.trim()) {
      errors.fullName = 'Full name is required';
    }
    
    if (!formData.phone.trim()) {
      errors.phone = 'Phone number is required';
    } else if (!/^\d{10,15}$/.test(formData.phone.replace(/\D/g, ''))) {
      errors.phone = 'Please enter a valid phone number';
    }
    
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }

    if (!acceptedPolicy) {
      errors.policy = 'You must accept our booking policy to continue';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleProceedToPayment = () => {
    if (validateForm()) {
      localStorage.setItem('customerDetails', JSON.stringify({
        fullName: formData.fullName,
        phone: formData.phone,
        email: formData.email,
      }));
      setShowPaymentSection(true);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validateForm()) {
      localStorage.setItem('customerDetails', JSON.stringify({
        fullName: formData.fullName,
        phone: formData.phone,
        email: formData.email,
      }));
      setShowPaymentSection(true);
    }
  };

  const handlePaymentClick = () => {
    window.open('https://monzo.com/pay/r/sheedah-beauty-limited_eSHauJWgan9MhK', '_blank');
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    setUploadError(null);

    if (!file) {
      setSelectedFile(null);
      return;
    }

    if (!file.type.startsWith('image/')) {
      setUploadError('Please select an image file (JPG, PNG, or GIF)');
      setSelectedFile(null);
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setUploadError('File size must be less than 10MB');
      setSelectedFile(null);
      return;
    }

    setSelectedFile(file);
  };

  const { isLocked } = useWebsiteLock();

  const handleBookingSubmit = async () => {
    if (!selectedFile || !selectedService) return;

    try {
      setIsSubmitting(true);
      setUploadError(null);

      const formDataToSubmit = new FormData();
      formDataToSubmit.append('action', 'submitBooking');
      formDataToSubmit.append('storeId', '372');
      formDataToSubmit.append('receipt', selectedFile);
      formDataToSubmit.append('serviceId', selectedService.id.toString());
      formDataToSubmit.append('bookingDate', format(selectedDate, 'yyyy-MM-dd'));
      formDataToSubmit.append('startTime', selectedTime);
      formDataToSubmit.append('customerName', formData.fullName);
      formDataToSubmit.append('customerEmail', formData.email);
      formDataToSubmit.append('customerPhone', formData.phone);
      formDataToSubmit.append('partySize', '1');
      
      if (formData.notes) {
        formDataToSubmit.append('notes', formData.notes);
      }

      const response = await submitBooking(formDataToSubmit);
      
      if (response.success) {
        navigate('/confirmation', { 
          state: { 
            service: selectedService,
            date: selectedDate,
            time: selectedTime,
            ...formData
          },
          replace: true
        });
      }
    } catch (error) {
      console.error('Error submitting booking:', error);
      setUploadError('Failed to submit booking. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const LockBanner: React.FC = () => {
    const { isLocked, message, refresh, loading, error } = useWebsiteLock() as any;
    if (!isLocked) return null;

    return (
      <div className="container-custom mb-6">
        <div className="p-4 rounded-lg bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 flex items-start justify-between">
          <div>
            <h4 className="font-semibold">Bookings are currently disabled</h4>
            <p className="text-sm text-gray-700 dark:text-gray-300">{message}</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              className="px-3 py-2 bg-primary-600 text-white rounded-lg"
              onClick={() => refresh && refresh()}
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="pt-24 pb-16">
      <div className="bg-primary-600 text-white py-12 mb-12">
        <div className="container-custom">
          <h1 className="text-4xl font-bold mb-4">Book Your Appointment</h1>
          <p className="text-xl opacity-90 max-w-2xl">
            {currentStep === 1 && 'Choose your preferred style to begin booking.'}
            {currentStep === 2 && 'Select your preferred date and time.'}
            {currentStep === 3 && 'Complete your booking details.'}
          </p>
        </div>
      </div>

      <LockBanner />

      <div className="container-custom">
        <div className="mb-12">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6">
            <h2 className="text-2xl font-bold mb-4 md:mb-0">
              {currentStep === 1 && 'Choose Your Style'}
              {currentStep === 2 && 'Choose Date & Time'}
              {currentStep === 3 && 'Your Information'}
            </h2>
            
            <div className="flex items-center">
              <div className={`flex items-center justify-center w-8 h-8 rounded-full ${
                currentStep >= 1 ? 'bg-primary-600 text-white' : 'bg-gray-200 dark:bg-gray-700 text-gray-600'
              }`}>
                1
              </div>
              <div className={`h-1 w-8 ${
                currentStep >= 2 ? 'bg-primary-600' : 'bg-gray-200 dark:bg-gray-700'
              }`}></div>
              <div className={`flex items-center justify-center w-8 h-8 rounded-full ${
                currentStep >= 2 ? 'bg-primary-600 text-white' : 'bg-gray-200 dark:bg-gray-700 text-gray-600'
              }`}>
                2
              </div>
              <div className={`h-1 w-8 ${
                currentStep >= 3 ? 'bg-primary-600' : 'bg-gray-200 dark:bg-gray-700'
              }`}></div>
              <div className={`flex items-center justify-center w-8 h-8 rounded-full ${
                currentStep >= 3 ? 'bg-primary-600 text-white' : 'bg-gray-200 dark:bg-gray-700 text-gray-600'
              }`}>
                3
              </div>
            </div>
          </div>
        </div>

        {currentStep === 1 && (
          <div className="animate-fade-in">
            <ServiceSelector onServiceSelect={handleServiceSelect} />
          </div>
        )}

        {currentStep === 2 && (
          <div className="animate-fade-in">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div>
                <div className="flex items-center mb-4">
                  <Calendar className="w-5 h-5 text-primary-600 mr-2" />
                  <h3 className="text-xl font-medium">Select a Date</h3>
                </div>
                <BookingCalendar
                  onDateSelect={setSelectedDate}
                  selectedDate={selectedDate}
                  maxAdvanceBookingDays={maxAdvanceBookingDays}
                  lockedDates={lockedDates}
                  fullyBookedDates={fullyBookedDates}
                />
              </div>

              <div>
                <div className="flex items-center mb-4">
                  <Clock className="w-5 h-5 text-primary-600 mr-2" />
                  <h3 className="text-xl font-medium">
                    Available Times {selectedDate && `for ${format(selectedDate, 'MMMM d, yyyy')}`}
                  </h3>
                </div>
                {selectedDate && (
                  <TimeSlotPicker 
                    onTimeSelect={(time) => {
                      setSelectedTime(time);
                      setCurrentStep(3);
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }} 
                    selectedDate={selectedDate}
                    settings={timeSlotSettings}
                  />
                )}
              </div>
            </div>

            <div className="mt-8 pt-8 border-t border-gray-200 dark:border-gray-800">
              <div className="flex items-center mb-4">
                <Info className="w-5 h-5 text-primary-600 mr-2" />
                <h3 className="text-xl font-medium">Booking Summary</h3>
              </div>
              
              <div className="card p-6">
                {selectedService && (
                  <div className="mb-4">
                    <p className="text-gray-600 dark:text-gray-400 mb-1">Service:</p>
                    <div className="flex items-start">
                      <img 
                        src={selectedService.image || '/img/placeholder.svg'} 
                        alt={selectedService.name}
                        className="w-20 h-20 object-cover rounded-lg mr-4"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.src = '/img/placeholder.svg';
                        }}
                      />
                      <div>
                        <p className="font-medium text-lg">{selectedService.name}</p>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">{selectedService.description}</p>
                        <div className="flex items-center gap-4">
                          <span className="text-sm bg-primary-100 dark:bg-primary-900/30 text-primary-800 dark:text-primary-300 px-3 py-1 rounded-full">
                            {selectedService.duration}
                          </span>
                          <span className="text-sm font-medium">£{selectedService.price}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                {selectedDate && (
                  <div className="mb-4">
                    <p className="text-gray-600 dark:text-gray-400 mb-1">Date:</p>
                    <p className="font-medium">{format(selectedDate, 'MMMM d, yyyy')}</p>
                  </div>
                )}
                {selectedTime && (
                  <div>
                    <p className="text-gray-600 dark:text-gray-400 mb-1">Time:</p>
                    <p className="font-medium">{selectedTime}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {currentStep === 3 && (
          <div className="animate-fade-in">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2">
                <form onSubmit={handleSubmit}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label htmlFor="fullName" className="form-label">
                        <User className="w-4 h-4 inline-block mr-2" />
                        Full Name
                      </label>
                      <input
                        type="text"
                        id="fullName"
                        name="fullName"
                        value={formData.fullName}
                        onChange={handleInputChange}
                        className={`form-input ${formErrors.fullName ? 'border-error-500 focus:ring-error-500' : ''}`}
                        placeholder="Enter your full name"
                      />
                      {formErrors.fullName && (
                        <p className="form-error">{formErrors.fullName}</p>
                      )}
                    </div>

                    <div>
                      <label htmlFor="phone" className="form-label">
                        <Phone className="w-4 h-4 inline-block mr-2" />
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className={`form-input ${formErrors.phone ? 'border-error-500 focus:ring-error-500' : ''}`}
                        placeholder="Enter your phone number"
                      />
                      {formErrors.phone && (
                        <p className="form-error">{formErrors.phone}</p>
                      )}
                    </div>
                  </div>

                  <div className="mb-6">
                    <label htmlFor="email" className="form-label">
                      <Mail className="w-4 h-4 inline-block mr-2" />
                      Email Address
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className={`form-input ${formErrors.email ? 'border-error-500 focus:ring-error-500' : ''}`}
                      placeholder="Enter your email address"
                    />
                    {formErrors.email && (
                      <p className="form-error">{formErrors.email}</p>
                    )}
                  </div>

                  <div className="mb-6">
                    <label htmlFor="notes" className="form-label">
                      Additional Notes (Optional)
                    </label>
                    <textarea
                      id="notes"
                      name="notes"
                      value={formData.notes}
                      onChange={handleInputChange}
                      className="form-input h-24 resize-none"
                      placeholder="Any special requests or information we should know"
                    ></textarea>
                  </div>

                  <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <h4 className="font-medium mb-4">Booking Policy</h4>
                     <div className="text-sm text-gray-600 dark:text-gray-400 space-y-2">
                      <p>• A £20 deposit is required to secure your booking</p>
                      <p>• The remaining balance is to be paid in either cash or bank transfer on the day of your appointment</p>
                      <p>• Cancellations must be made at least 24 hours before your appointment</p>
                      <p>• Late arrivals may result in reduced service time</p>
                      <p>• This is a home salon - all appointments take place at my home salon in Mutley, Plymouth</p>
                      <p>• Prices may vary based on hair length and thickness</p>
                    </div>
                    <div className="mt-4">
                      <label className="flex items-start cursor-pointer">
                        <input
                          type="checkbox"
                          checked={acceptedPolicy}
                          onChange={(e) => setAcceptedPolicy(e.target.checked)}
                          className="mt-1"
                        />
                        <span className="ml-2 text-sm">
                          I have read and agree to the booking policy
                        </span>
                      </label>
                      {formErrors.policy && (
                        <p className="form-error mt-1">{formErrors.policy}</p>
                      )}
                    </div>
                  </div>

                  {showPaymentSection ? (
                    <div>
                      {bookingSubmitted ? (
                        <div className="bg-warning-50 dark:bg-warning-900/20 p-6 rounded-lg mb-6 border border-warning-200 dark:border-warning-800">
                          <h4 className="text-lg font-medium mb-4 text-warning-700 dark:text-warning-300">
                            Booking Under Review
                          </h4>
                          <div className="flex items-center mb-4">
                            <Clock className="w-5 h-5 text-warning-600 mr-2" />
                            <span className="font-medium">We'll confirm within 24 hours</span>
                          </div>
                          <p className="mb-4 text-gray-600 dark:text-gray-400">
                            Your booking request has been submitted! You will receive a confirmation email once accepted.
                          </p>
                          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-warning-200">
                            <p className="text-sm">Booking Reference: <strong>#{submittedBookingId}</strong></p>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="animate-pulse mb-6 rounded-xl overflow-hidden">
                            <div className="bg-gradient-to-r from-warning-500 to-warning-600 p-6 text-white shadow-lg">
                              <div className="flex items-center mb-3">
                                <AlertCircle className="w-8 h-8 mr-3" />
                                <h4 className="text-2xl font-bold">Booking Not Yet Confirmed</h4>
                              </div>
                              <p className="text-lg text-warning-100 mb-2">
                                Your appointment is <strong>not active</strong> until payment is verified.
                              </p>
                              <p className="text-warning-50">
                                After paying the deposit, upload your receipt below. We will review and send you a confirmation email. The remaining balance will be paid in either cash or bank transfer on the day.
                              </p>
                            </div>
                          </div>
                          
                          <div className="bg-error-50 dark:bg-error-900/20 p-6 rounded-lg border-2 border-error-400 dark:border-error-600 shadow-lg mb-6 animate-bounce">
                            <div className="flex items-center mb-3">
                              <Upload className="w-8 h-8 text-error-600 mr-3" />
                              <h4 className="text-xl font-bold text-error-700 dark:text-error-300">
                                COME BACK TO THIS PAGE AFTER PAYMENT
                              </h4>
                            </div>
                            <p className="text-error-800 dark:text-error-200 font-semibold mb-2">
                              Paying is not enough! You must upload your receipt here to confirm your booking.
                            </p>
                            <p className="text-error-700 dark:text-error-300">
                              After you pay on Monzo, <strong>return to this tab</strong> and click <strong>"Upload Receipt"</strong>, then click <strong>"Submit Booking"</strong>. 
                              Without this step, your appointment is <strong>NOT booked</strong>.
                            </p>
                          </div>
                          
                          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border-2 border-warning-300 dark:border-warning-700 shadow-md">
                            <h4 className="text-lg font-semibold mb-4 text-gray-900 dark:text-gray-100">
                              Complete Your Booking
                            </h4>
                            <div className="flex items-center mb-4">
                              <PoundSterling className="w-6 h-6 text-warning-600 mr-2" />
                              <span className="font-medium text-lg">£20 Deposit Required</span>
                            </div>
                            <p className="mb-4 text-gray-600 dark:text-gray-400">
                              Click below to pay your deposit on Monzo. After payment, save your receipt 
                              and upload it below to confirm your booking. The remaining balance 
                              will be paid in either cash or bank transfer on the day of your appointment.
                            </p>
                            
                            <div className="flex flex-col sm:flex-row gap-4">
                              <button
                                type="button"
                                onClick={handlePaymentClick}
                                className="btn-primary flex-1 flex items-center justify-center"
                                disabled={isSubmitting || isLocked}
                              >
                                <Link className="w-4 h-4 mr-2" />
                                Pay £20 Deposit on Monzo
                              </button>

                              <div className="flex-1">
                                <input
                                  type="file"
                                  id="receipt"
                                  accept="image/*"
                                  onChange={handleFileChange}
                                  className="hidden"
                                />
                                <label
                                  htmlFor="receipt"
                                  className={`btn-outline w-full flex items-center justify-center cursor-pointer ${
                                    selectedFile ? 'border-success-500 text-success-600' : ''
                                  }`}
                                  aria-disabled={isLocked}
                                >
                                  {selectedFile ? (
                                    <>
                                      <Upload className="w-4 h-4 mr-2" />
                                      {selectedFile.name}
                                    </>
                                  ) : (
                                    <>
                                      <Upload className="w-4 h-4 mr-2" />
                                      Upload Receipt
                                    </>
                                  )}
                                </label>
                              </div>
                            </div>

                        {uploadError && (
                          <div className="mt-4 p-4 bg-error-50 dark:bg-error-900/20 border border-error-200 dark:border-error-800 rounded-lg">
                            <div className="flex items-start space-x-2 text-error-600 dark:text-error-400">
                              <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                              <div className="flex-1">
                                <span className="text-sm">{uploadError}</span>
                                <button
                                  onClick={() => {
                                    setUploadError(null);
                                    handleSubmit();
                                  }}
                                  disabled={isSubmitting}
                                  className="mt-2 inline-flex items-center px-3 py-1.5 border border-error-300 dark:border-error-700 text-xs font-medium rounded text-error-700 dark:text-error-300 bg-white dark:bg-error-900/50 hover:bg-error-50 dark:hover:bg-error-900/70 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-error-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  {isSubmitting ? (
                                    <>
                                      <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                      Retrying...
                                    </>
                                  ) : (
                                    'Try Again'
                                  )}
                                </button>
                              </div>
                            </div>
                          </div>
                        )}

                          {selectedFile && !uploadError && (
                            <button
                              type="button"
                              onClick={handleBookingSubmit}
                              disabled={isSubmitting || isLocked}
                              className="w-full mt-4 btn-primary flex items-center justify-center"
                            >
                              {isSubmitting ? (
                                <>
                                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                  Submitting...
                                </>
                              ) : (
                                isLocked ? 'Booking Disabled' : 'Submit Booking'
                              )}
                            </button>
                          )}
                        </div>
                        </>
                      )}
                    </div>
                  ) : (
                    <div className="flex items-center justify-between mt-8">
                      <button
                        type="button"
                        onClick={() => setCurrentStep(2)}
                        className="btn-outline"
                      >
                        Back
                      </button>
                      <button
                        type="button"
                        onClick={handleProceedToPayment}
                        className="btn-primary"
                        disabled={isLocked}
                      >
                        {isLocked ? 'Booking Disabled' : 'Proceed to Payment'}
                      </button>
                    </div>
                  )}
                </form>
              </div>

              <div>
                <div className="card p-6 sticky top-24">
                  <h3 className="text-lg font-medium mb-4">Booking Summary</h3>
                  
                  <div className="space-y-4">
                    {selectedService && (
                      <div>
                        <p className="text-gray-600 dark:text-gray-400 text-sm">Service:</p>
                        <div className="flex items-start">
                          <img 
                            src={selectedService.image || '/img/placeholder.svg'} 
                            alt={selectedService.name}
                            className="w-16 h-16 object-cover rounded-lg mr-3"
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.src = '/img/placeholder.svg';
                            }}
                          />
                          <div>
                            <p className="font-medium">{selectedService.name}</p>
                            <div className="flex items-center gap-3 mt-1">
                              <span className="text-sm bg-primary-100 dark:bg-primary-900/30 text-primary-800 dark:text-primary-300 px-2 py-0.5 rounded-full">
                                {selectedService.duration}
                              </span>
                              <span className="text-sm font-medium">£{selectedService.price}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                    {selectedDate && (
                      <div>
                        <p className="text-gray-600 dark:text-gray-400 text-sm">Date:</p>
                        <p className="font-medium">{format(selectedDate, 'MMMM d, yyyy')}</p>
                      </div>
                    )}
                    {selectedTime && (
                      <div>
                        <p className="text-gray-600 dark:text-gray-400 text-sm">Time:</p>
                        <p className="font-medium">{selectedTime}</p>
                      </div>
                    )}
                  </div>

                  <hr className="my-4 border-gray-200 dark:border-gray-700" />

                  <div>
                    <p className="text-gray-600 dark:text-gray-400 text-sm mb-1">What's next?</p>
                    <p className="text-sm">
                      Pay the £20 deposit, then upload your receipt. Your booking is only active once we confirm by email. 
                      The remaining balance is paid in either cash or bank transfer on the day.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BookingPage;
